import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import StatusBadge from "../components/StatusBadge";

const CATEGORY_LABELS = { ski: "Ski", boots: "Schuhe", poles: "Stöcke", binding: "Bindung", lurk: "Lurk" };

export default function ItemDetail() {
  const { itemId } = useParams();
  const { isAdmin } = useAuth();
  const [item, setItem] = useState(null);
  const [locations, setLocations] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [it, locs, allRentals] = await Promise.all([
        api.getItem(itemId),
        api.listLocations().catch(() => []),
        api.listRentals({ limit: 500 }),
      ]);
      setItem(it);
      setLocations(locs);
      const itemHistory = [];
      for (const r of allRentals) {
        for (const a of r.equipment_associations || []) {
          if (a.item_id === itemId) {
            itemHistory.push({ rental: r, assoc: a });
          }
        }
      }
      itemHistory.sort((a, b) => (b.assoc.rented_at || "").localeCompare(a.assoc.rented_at || ""));
      setHistory(itemHistory);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const startEdit = () => setEdit({
    status: item.status,
    serial_number: item.serial_number || "",
    condition_notes: item.condition_notes || "",
    owner: item.owner || "",
    home_location_id: item.home_location_id || "",
  });

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.updateItem(item.id, {
        status: edit.status,
        serial_number: edit.serial_number || null,
        condition_notes: edit.condition_notes || null,
        owner: edit.owner || null,
        home_location_id: edit.home_location_id || null,
      });
      setEdit(null);
      await reload();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-fh-gray">Lade…</div>;
  if (!item) return <div className="text-fh-gray">Item nicht gefunden.</div>;

  const eq = item.equipment;

  return (
    <div className="space-y-4">
      <Link to="/items" className="text-sm text-fh-gray hover:text-fh-black">← Zurück zum Inventar</Link>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-4">
          <div className="card p-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-mono">{item.id}</h1>
                <StatusBadge status={item.status} />
              </div>
              {isAdmin && !edit && <button onClick={startEdit} className="btn-secondary text-sm">Bearbeiten</button>}
            </div>

            {!edit ? (
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Field label="Kategorie" value={CATEGORY_LABELS[eq?.category] || eq?.category} />
                <Field label="Modell" value={[eq?.brand, eq?.model, eq?.size].filter(Boolean).join(" ")} />
                <Field label="Seriennummer" value={item.serial_number || "—"} />
                <Field label="Eigentümer" value={item.owner || "—"} />
                <Field label="Heimat-Standort" value={item.home_location?.name || "—"} />
                <Field label="Zugang" value={item.acquired_at ? new Date(item.acquired_at).toLocaleDateString("de-DE") : "—"} />
                {item.condition_notes && <Field label="Zustand / Notiz" value={item.condition_notes} className="col-span-2" />}
              </dl>
            ) : (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-field">Status</label>
                  <select className="input-field" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                    <option value="available">Verfügbar</option>
                    <option value="rented">Verliehen</option>
                    <option value="maintenance">Wartung</option>
                    <option value="retired">Ausgemustert</option>
                  </select>
                </div>
                <div>
                  <label className="label-field">Heimat-Standort</label>
                  <select className="input-field" value={edit.home_location_id} onChange={(e) => setEdit({ ...edit, home_location_id: e.target.value })}>
                    <option value="">—</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-field">Seriennummer</label>
                  <input className="input-field" value={edit.serial_number} onChange={(e) => setEdit({ ...edit, serial_number: e.target.value })} />
                </div>
                <div>
                  <label className="label-field">Eigentümer (z.B. Partner)</label>
                  <input className="input-field" value={edit.owner} onChange={(e) => setEdit({ ...edit, owner: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-field">Zustand / Notiz</label>
                  <textarea
                    className="input-field"
                    rows={2}
                    value={edit.condition_notes}
                    onChange={(e) => setEdit({ ...edit, condition_notes: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                  <button className="btn-secondary" onClick={() => setEdit(null)} disabled={saving}>Abbrechen</button>
                  <button className="btn-primary" onClick={saveEdit} disabled={saving}>
                    {saving ? "Speichere…" : "Speichern"}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="card overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200">
              <h2 className="font-semibold">Verleih-Historie ({history.length})</h2>
            </div>
            {history.length === 0 ? (
              <div className="p-6 text-center text-fh-gray text-sm">Noch nie verliehen.</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {history.map(({ rental, assoc }) => (
                  <li key={assoc.id} className="px-5 py-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <Link to={`/rentals/${rental.rental_id}`} className="font-medium hover:underline">
                        {rental.first_name} {rental.last_name}
                      </Link>
                      <span className="text-xs text-fh-gray font-mono">{rental.rental_id}</span>
                    </div>
                    <div className="text-xs text-fh-gray mt-1">
                      {assoc.rented_at && new Date(assoc.rented_at).toLocaleString("de-DE")}
                      {assoc.rented_by_user_id && ` · ausgegeben von ${assoc.rented_by_user_id}`}
                      {assoc.returned_at && ` · zurück ${new Date(assoc.returned_at).toLocaleString("de-DE")}`}
                      {assoc.returned_by_user_id && ` · ${assoc.returned_by_user_id}`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="font-semibold mb-3">QR-Code</h3>
            <img
              src={api.itemQrUrl(item.id)}
              alt={`QR ${item.id}`}
              className="w-full h-auto bg-fh-gray-light rounded"
            />
            <a
              href={api.itemQrUrl(item.id)}
              download={`${item.id}.png`}
              className="btn-secondary w-full mt-3 text-sm"
            >
              ⬇️ Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-wider text-fh-gray font-semibold">{label}</div>
      <div className="mt-0.5 break-words">{value}</div>
    </div>
  );
}
